import { useEffect, useState } from "react";
import { Copy, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import IcalStatus from "@/pages/admin/IcalStatus";

const Field = ({ label, testid, value, onChange, type = "text", step, placeholder, hint }) => (
  <label className="block text-sm">
    <span className="mb-1.5 block text-xs font-medium text-bone">{label}</span>
    <input type={type} step={step} className="field !py-2" data-testid={testid} value={value}
      placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);

const IcalExport = ({ gites }) => (
  <div className="border-t border-white/10 pt-5">
    <h4 className="text-sm font-semibold text-bone">Export vers Airbnb et Booking.com</h4>
    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
      Un lien par plateforme : collez le lien « Airbnb » dans Airbnb (« Disponibilités » →
      « Synchroniser les calendriers » → « Importer un calendrier ») et le lien « Booking.com » dans
      l'Extranet Booking (« Tarifs & Disponibilités » → « Synchroniser les calendriers »). Chaque lien
      contient les réservations du site, les nuits bloquées dans l'admin, les nuits vendues sur
      l'autre gîte ET les nuits vendues sur l'autre plateforme — sans renvoyer à chaque plateforme
      ses propres dates.
    </p>
    <p className="mt-2 text-[11px] leading-relaxed text-brass" data-testid="ical-delay-note">
      À savoir : les plateformes ne relisent ces liens que toutes les quelques heures. La
      synchronisation iCal réduit fortement le risque de double réservation mais ne peut pas le
      supprimer totalement — pour des dates très proches, vérifiez le calendrier avant de confirmer.
    </p>
    {gites.map((g) => (
      <div key={g.slug} className="mt-3 bg-white/5 p-2.5 text-xs" data-testid={`export-ical-${g.slug}`}>
        <p className="mb-1.5 font-medium text-bone">{g.name}</p>
        {[["airbnb", "Airbnb"], ["booking", "Booking.com"]].map(([key, label]) => {
          const url = `${API}/ical/${g.slug}.ics?for=${key}`;
          return (
            <div key={key} className="mt-1.5 flex min-w-0 items-center gap-2"
              data-testid={`export-ical-${key}-${g.slug}`}>
              <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
              <span className="min-w-0 flex-1 break-all leading-snug">{url}</span>
              <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Lien copié"); }}
                data-testid={`copy-ical-${key}-${g.slug}`} className="shrink-0 text-bone"><Copy size={14} /></button>
              <a href={url} target="_blank" rel="noreferrer" className="shrink-0 text-bone"><ExternalLink size={14} /></a>
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

export default function SettingsTab({ gites, onSync }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncKey, setSyncKey] = useState(0);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-sm text-muted-foreground">Chargement…</p>;

  const set = (patch) => setS({ ...s, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        cleaning_fee_short: Number(s.cleaning_fee_short), cleaning_fee_long: Number(s.cleaning_fee_long),
        cleaning_threshold: Number(s.cleaning_threshold || 3),
        deposit_amount: Number(s.deposit_amount), deposit_days: Number(s.deposit_days || 7),
        deposit_amounts: Object.fromEntries(Object.entries(s.deposit_amounts || {})
          .filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)])),
        min_nights: Number(s.min_nights),
        tourist_tax_rate: Number(s.tourist_tax_rate),
        tourist_tax_collector: s.tourist_tax_collector, tourist_tax_portal: s.tourist_tax_portal,
        owner_email: s.owner_email,
        access_codes: s.access_codes || {},
        access_code_rotation_days: Number(s.access_code_rotation_days || 90),
        access_code_lead_hours: Number(s.access_code_lead_hours || 48),
        refund_full_days: Number(s.refund_full_days || 14),
        refund_half_days: Number(s.refund_half_days || 7),
        ical_urls: s.ical_urls, ical_urls_booking: s.ical_urls_booking || {},
        contact_email: s.contact_email, contact_phone: s.contact_phone,
        address: s.address, instagram: s.instagram,
      });
      toast.success("Réglages enregistrés");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const units = gites.filter((g) => !g.composite.length);

  return (
    <div className="grid gap-8 [&>div]:min-w-0 lg:grid-cols-2" data-testid="settings-tab">
      <div className="space-y-5 border border-white/10 bg-panel p-6">
        <h3 className="font-display text-xl text-bone">Tarification</h3>
        <Field label="Ménage 1 à 2 nuits (€)" testid="setting-cleaning-short" type="number"
          value={s.cleaning_fee_short} onChange={(v) => set({ cleaning_fee_short: v })} />
        <Field label="Ménage 3 nuits et plus (€)" testid="setting-cleaning-long" type="number"
          value={s.cleaning_fee_long} onChange={(v) => set({ cleaning_fee_long: v })} />
        <Field label="Caution par défaut (empreinte bancaire, €)" testid="setting-deposit" type="number"
          value={s.deposit_amount} onChange={(v) => set({ deposit_amount: v })} />
        <p className="text-xs leading-relaxed text-muted-foreground">
          Caution par gîte — le lien d'empreinte bancaire est envoyé automatiquement au voyageur
          2 jours avant son arrivée, et la somme est libérée 48 h après son départ.
        </p>
        {gites.map((g) => (
          <Field key={g.slug} label={`Caution — ${g.name} (€)`} type="number"
            testid={`setting-deposit-${g.slug}`} placeholder={String(s.deposit_amount || 350)}
            value={s.deposit_amounts?.[g.slug] ?? ""}
            onChange={(v) => set({ deposit_amounts: { ...(s.deposit_amounts || {}), [g.slug]: v } })} />
        ))}
        <Field label="Nuits minimum" testid="setting-min-nights" type="number"
          value={s.min_nights} onChange={(v) => set({ min_nights: v })} />

        <h3 className="pt-3 font-display text-xl text-bone">Codes d'accès (boîte à clés)</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Envoyés automatiquement {s.access_code_lead_hours || 48} h avant chaque arrivée. Un rappel vous
          est adressé tous les {s.access_code_rotation_days || 90} jours pour renouveler le code.
        </p>
        {units.map((g) => (
          <Field key={g.slug} label={g.name} testid={`setting-code-${g.slug}`} placeholder="ex : 1408"
            value={s.access_codes?.[g.slug] || ""}
            hint={s.access_code_set_at?.[g.slug] ? `défini le ${s.access_code_set_at[g.slug]}` : null}
            onChange={(v) => set({ access_codes: { ...(s.access_codes || {}), [g.slug]: v } })} />
        ))}
        <Field label="Email propriétaire (validations & sauvegardes)" testid="setting-owner-email"
          value={s.owner_email} onChange={(v) => set({ owner_email: v })} />

        <h3 className="pt-3 font-display text-xl text-bone">Taxe de séjour</h3>
        <Field label="Tarif par personne et par nuit (€) — meublé 4★" testid="setting-tax-rate" type="number" step="0.01"
          value={s.tourist_tax_rate} onChange={(v) => set({ tourist_tax_rate: v })} />
        <Field label="Organisme collecteur" testid="setting-tax-collector"
          value={s.tourist_tax_collector} onChange={(v) => set({ tourist_tax_collector: v })} />
        <Field label="Portail de déclaration" testid="setting-tax-portal"
          value={s.tourist_tax_portal} onChange={(v) => set({ tourist_tax_portal: v })} />

        <h3 className="pt-3 font-display text-xl text-bone">Contact affiché</h3>
        <input className="field !py-2" data-testid="setting-email" value={s.contact_email} onChange={(e) => set({ contact_email: e.target.value })} />
        <input className="field !py-2" data-testid="setting-phone" value={s.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })} />
        <input className="field !py-2" data-testid="setting-address" value={s.address} onChange={(e) => set({ address: e.target.value })} />
        <input className="field !py-2" data-testid="setting-instagram" value={s.instagram || ""} onChange={(e) => set({ instagram: e.target.value })} />
        <button onClick={save} disabled={saving} className="btn-primary !py-2.5" data-testid="save-settings-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : null} Enregistrer
        </button>
      </div>

      <div className="space-y-5 border border-white/10 bg-panel p-6">
        <h3 className="font-display text-xl text-bone">Synchronisation Airbnb & Booking</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Collez ici les liens iCal d'export Airbnb (Calendrier → Disponibilités → Synchroniser) et
          Booking.com, pour chacune des trois annonces — y compris l'annonce groupée « Le Chalet + La Ferme ».
          Une date bloquée sur l'annonce groupée bloque aussi les deux gîtes individuels, et inversement.
          Les tarifs, eux, restent gérés sur ce site.
        </p>
        {gites.map((g) => (
          <div key={g.slug} className="space-y-2">
            <Field label={`${g.name} — iCal Airbnb`} testid={`ical-${g.slug}`}
              placeholder="https://www.airbnb.fr/calendar/ical/....ics"
              value={s.ical_urls?.[g.slug] || ""}
              onChange={(v) => set({ ical_urls: { ...s.ical_urls, [g.slug]: v } })} />
            <Field label={`${g.name} — iCal Booking.com`} testid={`ical-booking-${g.slug}`}
              placeholder="https://admin.booking.com/hotel/...ics"
              value={s.ical_urls_booking?.[g.slug] || ""}
              onChange={(v) => set({ ical_urls_booking: { ...(s.ical_urls_booking || {}), [g.slug]: v } })} />
          </div>
        ))}
        <div className="flex flex-wrap gap-3">
          <button onClick={save} className="btn-ghost !py-2.5" data-testid="save-ical-btn">Enregistrer les liens</button>
          <button onClick={async () => { await onSync?.(); setSyncKey((k) => k + 1); }}
            className="btn-primary !py-2.5" data-testid="sync-now-btn"><RefreshCw size={15} /> Synchroniser</button>
        </div>

        <div className="mt-6 border border-white/10 bg-white/[0.02] p-4 text-[11px] leading-relaxed text-muted-foreground">
          Politique d'annulation appliquée automatiquement : 100 % de remboursement à plus de
          {" "}{s.refund_full_days || 14} jours de l'arrivée, 50 % entre {s.refund_full_days || 14} et
          {" "}{s.refund_half_days || 7} jours, 0 % en dessous. La taxe de séjour est toujours restituée.
        </div>

        <IcalStatus reloadKey={syncKey} />

        <IcalExport gites={gites} />
      </div>
    </div>
  );
}
